import React, { useState } from "react";
import {
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  useTheme,
  useMediaQuery,
  TextField,
  Tooltip
} from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sharedStyles } from '../styles/sharedStyles';
import { planGroups } from '../../utils/planConfig';

const incomeBrackets = [
  { value: '', label: 'Select bracket' },
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
];

const Step4PlanInfo = ({ 
  formData = {}, 
  setFormData, 
  errors = {}, 
  setErrors, 
  handleNext, 
  handleBack 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handlePlanChange = (e) => {
    setFormData({
      ...formData,
      recommendedMedicalAidPlan: e.target.value,
      ...(e.target.value !== 'PL470' ? { incomeAmount: '', incomeBracket: '' } : {})
    });
  };

  const handleIncomeChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date) => {
    let formatted = '';
    if (date instanceof Date && !isNaN(date)) {
      formatted = date.toISOString().split('T')[0];
    }
    setFormData({ ...formData, medstart_date: formatted });
  };

  const onNext = (e) => {
    e.preventDefault();
    handleNext();
  };

  return (
    <Box component="form" onSubmit={onNext} autoComplete="off" sx={{ ...sharedStyles.formContainer, maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Section Header: Start Date */}
      <Paper sx={{
        backgroundColor: '#ba0d36',
        color: '#fff',
        borderRadius: 3,
        mb: 3,
        p: 1.5,
        textAlign: 'center',
        fontWeight: 700,
        fontSize: { xs: '1.1rem', sm: '1.2rem' },
        letterSpacing: 1
      }} elevation={0}>
        CHOOSE A START DATE
      </Paper>
      <Box sx={{ mb: 4 }}>
        <FormControl fullWidth error={!!errors.medstart_date}>
          <FormLabel sx={{ mb: 1, color: '#222', fontWeight: 600 }}>Medical Aid Start Date</FormLabel>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={formData.medstart_date ? new Date(formData.medstart_date) : null}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                  error: !!errors.medstart_date,
                  helperText: errors.medstart_date,
                  sx: { background: '#fff', borderRadius: 1 }
                }
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </Box>

      {/* Section Header: Choose Option */}
      <Paper sx={{
        backgroundColor: '#393636',
        color: '#fff',
        borderRadius: 3,
        mb: 3,
        p: 1.5,
        textAlign: 'center',
        fontWeight: 700,
        fontSize: { xs: '1.1rem', sm: '1.2rem' },
        letterSpacing: 1
      }} elevation={0}>
        CHOOSE AN OPTION
      </Paper>

      <RadioGroup
        name="recommendedMedicalAidPlan"
        value={formData.recommendedMedicalAidPlan || ''}
        onChange={handlePlanChange}
        sx={{ width: '100%' }}
      >
        <Grid container spacing={4}>
          {planGroups.map((group) => (
            <Grid item xs={12} key={group.label}>
              <Paper sx={{
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
                mb: 2,
                border: '1.5px solid #eee',
                boxShadow: 'none',
                minHeight: 80
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#393636', textAlign: 'center', letterSpacing: 1 }}>
                  {group.label}
                </Typography>
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} justifyContent="center" alignItems="center">
                  {group.options.map((option, idx) => {
                    if (option.value === 'PL470') {
                      // BonCap option with inline income input
                      return (
                        <Grid item xs={6} sm={4} md={2} key={option.value} sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
                          <FormControlLabel
                            value={option.value}
                            control={<Radio sx={{ color: '#ba0d36', '&.Mui-checked': { color: '#ba0d36' }, p: 0.5, '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                            label={<Typography sx={{ fontWeight: 400, fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: '#393636', textAlign: 'center', whiteSpace: 'nowrap' }}>{option.label}</Typography>}
                            labelPlacement="bottom"
                            sx={{ m: 0, px: 0.5, py: 0.5, borderRadius: 2, transition: 'none', border: 'none', background: 'none', width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center', flexDirection: 'column' }}
                          />
                          {formData.recommendedMedicalAidPlan === 'PL470' && (
                            <TextField
                              label="Income amount"
                              name="incomeAmount"
                              type="text"
                              value={formData.incomeAmount || ''}
                              onChange={handleIncomeChange}
                              size="small"
                              sx={{ width: 120, mt: 1 }}
                              error={!!errors.incomeAmount}
                              helperText={errors.incomeAmount}
                            />
                          )}
                        </Grid>
                      );
                    }
                    // All other options
                    return (
                      <Grid item xs={6} sm={4} md={2} key={option.value} sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
                        <FormControlLabel
                          value={option.value}
                          control={<Radio sx={{ color: '#ba0d36', '&.Mui-checked': { color: '#ba0d36' }, p: 0.5, '& .MuiSvgIcon-root': { fontSize: 20 } }} />}
                          label={<Typography sx={{ fontWeight: 400, fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: '#393636', textAlign: 'center', whiteSpace: 'nowrap' }}>{option.label}</Typography>}
                          labelPlacement="bottom"
                          sx={{ m: 0, px: 0.5, py: 0.5, borderRadius: 2, transition: 'none', border: 'none', background: 'none', width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center', flexDirection: 'column' }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </RadioGroup>
      {!!errors.recommendedMedicalAidPlan && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          {errors.recommendedMedicalAidPlan}
        </Typography>
      )}
    </Box>
  );
};

export default Step4PlanInfo; 